import type { CSSProperties } from "react";
import { EXAMPLE_SHORTLIST, listCounts, type ExampleShortlistState } from "@/lib/exampleTracking";
import { ROUTES } from "@/lib/routes";
import JobLine, { CARD } from "./JobLine";
import PlacePreview from "./PlacePreview";
import { CHIP } from "./TrackParts";

const SAVED_STATES: Record<
    ExampleShortlistState,
    { chip: string; chipClass: string; line: string }
> = {
    open: {
        chip: "Open",
        chipClass: "bg-applied text-applied-fg",
        line: "Still taking applications. Tailor when you are ready.",
    },
    tailoring: {
        chip: "Tailoring",
        chipClass: "bg-queued text-queued-fg",
        line: "A resume is being written for it, in Applications.",
    },
    closed: {
        chip: "Closed",
        chipClass: "bg-skipped text-skipped-fg",
        line: "The company took it down. Kept here so you know.",
    },
    applied: {
        chip: "Applied",
        chipClass: "bg-applied text-applied-fg",
        line: "Sent. The resume that went is in Applications.",
    },
};

const PILL = "rounded-full border px-3 py-1 text-[12.5px] font-semibold";

/** The lists, as the Saved page's bar shows them: all saved jobs first, then each list. */
function Lists() {
    return (
        <ul className="m-0 flex list-none flex-wrap gap-2 p-0" aria-label="Lists">
            <li className={`${PILL} border-fg bg-fg text-on-fg`}>
                All <span className="font-mono">{EXAMPLE_SHORTLIST.length}</span>
            </li>
            {listCounts().map((list) => (
                <li key={list.name} className={`${PILL} border-line bg-card text-fg-muted`}>
                    {list.name} <span className="font-mono">{list.count}</span>
                </li>
            ))}
        </ul>
    );
}

/** Saved: a shortlist in named lists, each job open, being tailored, closed, or sent. */
export default function SavedPreview() {
    return (
        <PlacePreview
            kind="saved"
            secondary={{ label: "Find jobs", to: ROUTES.HOME }}
            note="Saving never spends a tailor."
        >
            <Lists />
            <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
                {EXAMPLE_SHORTLIST.map((entry, index) => {
                    const state = SAVED_STATES[entry.state];
                    return (
                        <li
                            key={entry.job.company}
                            className={`${CARD} flow-step flex flex-wrap items-center gap-x-4 gap-y-2 px-[18px] py-4`}
                            style={{ "--step": index } as CSSProperties}
                        >
                            <div className="flex-[1_1_300px]">
                                <JobLine job={entry.job} after={entry.saved} />
                            </div>
                            <span className="flex-[1_1_220px] text-[13px] text-fg-muted">
                                {state.line}
                            </span>
                            <span className="shrink-0 rounded-[7px] border border-line px-2 py-[2px] text-[11.5px] font-medium text-fg-muted">
                                {entry.list}
                            </span>
                            <span className={`${CHIP} ${state.chipClass}`}>{state.chip}</span>
                        </li>
                    );
                })}
            </ul>
        </PlacePreview>
    );
}
